import React from 'react';
import { DollarSign, Mail, MapPin, Phone } from 'lucide-react';
import { FaLinkedinIn, FaInstagram, FaFacebookF, FaTiktok } from 'react-icons/fa';
import { isIosApp } from '../utils/platform';

export const SiteFooter = () => (
  <footer className="bg-[#064E3B] text-white py-12 relative z-10">
    <div className="container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-5 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#84CC16] to-[#65A30D] rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-black" strokeWidth={3} />
              </div>
              <span className="text-xl font-bold">MoneyFeed</span>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              The WhatsApp tool that works with your bookkeeping app — built for freelancers, small businesses and individuals. Track <a href="/expense-tracking" className="underline hover:text-[#84CC16] transition-colors">expenses</a>, <a href="/mileage-tracking" className="underline hover:text-[#84CC16] transition-colors">mileage</a> and taxes by text.
            </p>
            <div className="text-xs text-gray-400">
              © 2026 Maxe Feed Holdings LLC (MoneyFeed). All rights reserved.
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-bold mb-4 text-[#84CC16]">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/privacy" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Terms & Conditions
                </a>
              </li>
              <li>
                <a href="/refund" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Refund Policy
                </a>
              </li>
              <li>
                <a href="/data-protection" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Data Protection
                </a>
              </li>
            </ul>
          </div>

          {/* Product */}
          <div className="md:col-span-2">
            <h4 className="font-bold mb-4 text-[#84CC16]">Product</h4>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <li>
                <a href="/features" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Features
                </a>
              </li>
              <li>
                <a href="/tools" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Free Tools
                </a>
              </li>
              <li>
                <a href="/integrations" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Integrations
                </a>
              </li>
              <li>
                <a href="/supported-countries" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Supported Countries
                </a>
              </li>
              <li>
                <a href="/tax-support" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Tax Support
                </a>
              </li>
              <li>
                <a href="/security" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Security
                </a>
              </li>
              {!isIosApp() && (
              <li>
                <a href="/#pricing" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Pricing
                </a>
              </li>
              )}
              <li>
                <a href="/whatsapp-bookkeeping" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  WhatsApp Bookkeeping
                </a>
              </li>
              <li>
                <a href="/whatsapp-accounting" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  WhatsApp Accounting
                </a>
              </li>
              <li>
                <a href="/ai-bookkeeping" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  AI Bookkeeping
                </a>
              </li>
              <li>
                <a href="/expense-tracking" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Expense Tracking
                </a>
              </li>
              <li>
                <a href="/mileage-tracking" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Mileage Tracking
                </a>
              </li>
              <li>
                <a href="/freelancer-bookkeeping" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Freelancer Bookkeeping
                </a>
              </li>
              <li>
                <a href="/1099-bookkeeping" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  1099 Bookkeeping
                </a>
              </li>
              <li>
                <a href="/tools/1099-tax-calculator" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Free Tax Calculator
                </a>
              </li>
              <li>
                <a href="/#tax-guides" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Tax Guides by Country &amp; State
                </a>
              </li>
              <li>
                <a href="/blog" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Blog
                </a>
              </li>
              <li>
                <a href="/auth" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Get Started
                </a>
              </li>
              <li>
                <a href="mailto:feed@moneyfeed.io" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  Support
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold mb-4 text-[#84CC16]">Contact Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-[#84CC16] mt-0.5 flex-shrink-0" />
                <a href="mailto:feed@moneyfeed.io" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  feed@moneyfeed.io</a></li><li className="flex items-center gap-2"><a href="/contact" className="text-gray-300 hover:text-[#84CC16] transition-colors">Contact
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-[#84CC16] mt-0.5 flex-shrink-0" />
                <a href="tel:+18326886863" className="text-gray-300 hover:text-[#84CC16] transition-colors">
                  +1 (832) 688-6863
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-[#84CC16] mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">
                  5900 Balcones Drive 7274<br/>
                  Austin, TX 78731<br/>
                  United States
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div>
              <p className="mb-2">
                MoneyFeed is not affiliated with WhatsApp Inc. WhatsApp is a trademark of Meta Platforms, Inc.
              </p>
              <p>
                Your financial data is encrypted and secure. We do not share your information with third parties.
              </p>
            </div>
            <div className="flex gap-3">
              <a href="https://www.linkedin.com/company/moneyfeed" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-gray-300 hover:bg-[#84CC16] hover:text-black transition-colors">
                <FaLinkedinIn className="h-4 w-4" />
              </a>
              <a href="https://www.instagram.com/moneyfeed.io" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-gray-300 hover:bg-[#84CC16] hover:text-black transition-colors">
                <FaInstagram className="h-4 w-4" />
              </a>
              <a href="https://www.tiktok.com/@moneyfeed.ai" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-gray-300 hover:bg-[#84CC16] hover:text-black transition-colors">
                <FaTiktok className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/888438034354330" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-gray-300 hover:bg-[#84CC16] hover:text-black transition-colors">
                <FaFacebookF className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

export default SiteFooter;
